import { useContext } from "react";
import RorpContext from "../../contexts/RorpContext";
import { history } from "instantsearch.js/es/lib/routers";
import isSSR from "../isSSR";
import { InstantSearchProps } from "react-instantsearch";
import { sortRouteToStateHash, sortStateToRouteHash } from "./sorting";
import { useCategoryRouter } from "./categoryHierarchy";
import { GetSearchProps_categories } from "../../components/pages/SearchPage/queries/types/GetSearchProps";

export function useRouting(
  categories: GetSearchProps_categories[],
  indexName: string
): InstantSearchProps["routing"] {
  const rorpContext = useContext(RorpContext);
  const { mapCategoryStateToRoute, mapCategoryRouteToState } = useCategoryRouter(categories);

  return {
    router: history({
      cleanUrlOnDispose: false,
      getLocation: () => {
        if (isSSR()) {
          const { URL } = require("url");
          return new URL(rorpContext.href);
        }
        return window.location;
      },
    }),
    stateMapping: {
      stateToRoute(uiState): any {
        const { query, page, refinementList, range, sortBy, toggle } = uiState[indexName];

        return {
          q: query,
          page,
          sort: sortStateToRouteHash[sortBy],
          category: mapCategoryStateToRoute(uiState[indexName]),
          brand: refinementList?.brand?.join(","),
          size: refinementList?.size?.join(","),
          colour: refinementList?.colour?.join(","),
          pattern: refinementList?.pattern?.join(","),
          price: range?.price_aud,
          item_type: refinementList?.item_type?.join(","),
          condition: refinementList?.condition?.join(","),
          adaptive: toggle?.adaptive,
          material: refinementList?.material?.join(","),
          occasion: refinementList?.occasion?.join(","),
          style: refinementList?.style?.join(","),
          deals: refinementList?.price_reduced_percentage_bucket?.join(","),
        };
      },
      routeToState(routeState: any) {
        const {
          q,
          page,
          sort,
          category,
          brand,
          size,
          colour,
          pattern,
          item_type,
          condition,
          price,
          adaptive,
          material,
          occasion,
          style,
          price_reduced_percentage_bucket,
          deals,
        } = routeState;

        return {
          [indexName]: {
            query: q,
            page,
            sortBy: sortRouteToStateHash[sort],
            hierarchicalMenu: mapCategoryRouteToState(category),
            refinementList: {
              brand: brand?.split(","),
              size: size?.split(","),
              colour: colour?.split(","),
              pattern: pattern?.split(","),
              item_type: item_type?.split(","),
              condition: condition?.split(","),
              material: material?.split(","),
              occasion: occasion?.split(","),
              style: style?.split(","),
              price_reduced_percentage_bucket:
                deals?.split(",") || price_reduced_percentage_bucket?.split(","),
            },
            range: {
              price_aud: mapLegacyPriceRouteToState(price),
            },
            toggle: {
              adaptive: adaptive,
            },
          },
        };
      },
    },
  };
}

// add legacy support for price price=$100-$200,$0-$50
const mapLegacyPriceRouteToState = (price: string) => {
  if (!price) return undefined;

  if (!price.includes("$")) return price;

  // Legacy price url format
  if (price.includes("$")) {
    let min = null;
    let max = null;

    price.split(/[,|-]/).forEach((priceString) => {
      const priceInt = parseInt(priceString.replace(/[$+]/g, ""));
      if (priceString === "$500+") {
        max = Infinity;
      } else if (max == null || priceInt > max) {
        max = priceInt;
      }

      if (min === null || priceInt < min) {
        min = priceInt;
      }
    });

    if (max === Infinity) {
      return `${min}:`;
    }

    return `${min}:${max}`;
  }
};
